import React, { useContext, memo, useMemo, useState } from 'react'
import type { Genre, TransformedImageId } from '../../../grab/types'
import { Card, NavigationLink } from './components-lib'
import { StyleSheet } from '../stylesheet'
import { ResourceImage } from './resource-image'
import type { AppPath } from '../navigation'
import type {
  InfoBlockElementConfig,
  InfoBlockElementType,
  StaticEvent,
  StaticGroup,
  TileLayoutConfig,
  TileSlotPosition,
  TileFieldType,
  TileSlotConfig,
} from '../types'
import { MainConfig, PageScheduleConfig, useLang } from '../chapito-static'
import { formatDayLong, formatEndAndStartHour, formatHour, nowMs, showHour } from '../../../services/date'

const DEFAULT_INFO_BLOCK_ELEMENTS_EVENT: InfoBlockElementConfig[] = [
  { type: 'TITLE', enabled: true },
  { type: 'GUESTS', enabled: false },
  { type: 'DATE_TIME', enabled: true },
  { type: 'SCENE', enabled: true },
  { type: 'PRICE', enabled: true },
]

const DEFAULT_INFO_BLOCK_ELEMENTS_GUEST: InfoBlockElementConfig[] = [{ type: 'TITLE', enabled: true }]

// Default layout for events: TITLE at bottom center
export const DEFAULT_EVENT_TILE_LAYOUT: TileLayoutConfig = {
  slots: [{ position: 'BOTTOM_CENTER', field: 'TITLE' }],
}

// Default layout for guests: NAME at bottom center
export const DEFAULT_GUEST_TILE_LAYOUT: TileLayoutConfig = {
  slots: [{ position: 'BOTTOM_CENTER', field: 'NAME' }],
}

type TileCardProps = {
  event?: StaticEvent
  guest?: StaticGroup
  tileLayout?: TileLayoutConfig
  navLink?: AppPath
  style?: React.CSSProperties
}

export const TileCard: React.FC<TileCardProps> = memo(function TileCard({ event, guest, tileLayout, navLink, style }) {
  const lang = useLang()
  const config = useContext(MainConfig)
  const schedule = useContext(PageScheduleConfig)
  const dateFormatConfig = { lang, timezone: config.timezone }
  const [isHovered, setHovered] = useState(false)

  // Determine which layout to use
  const layout = tileLayout ?? (event ? DEFAULT_EVENT_TILE_LAYOUT : DEFAULT_GUEST_TILE_LAYOUT)

  // Image source. Shown either as background (legacy 3x3 overlay mode) or
  // anchored at the top above an info block — see renderTile() below.
  const backgroundImageEnabled = layout.showBackgroundImage !== false
  const image: TransformedImageId | undefined = backgroundImageEnabled ? event?.image ?? guest?.image : undefined

  const showInfoBlock = tileLayout !== undefined && tileLayout.showInfoBlock !== false
  const showEventGuests = layout.showEventGuests === true && !!event

  // Find the next upcoming event for a guest
  const nextEvent = useMemo(
    () => guest?.relatedEvents?.filter((e) => e.showStartDate > nowMs()).sort((a, b) => a.showStartDate - b.showStartDate)[0] ?? null,
    [guest?.relatedEvents]
  )

  // Get field value based on field type
  const getFieldValue = (field: TileFieldType, tagCategoryName?: string): React.ReactNode => {
    switch (field) {
      case 'TITLE':
        return event?.title ?? null

      case 'NAME':
        return guest?.name?.toLocaleUpperCase(lang) ?? null

      case 'TIME': {
        if (!event) return null
        const timeStr = showHour(event.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(event, dateFormatConfig, schedule) : ''
        return timeStr || null
      }

      case 'TAGS': {
        let genres: Genre[] = event?.genres ?? guest?.genres ?? []
        if (tagCategoryName && genres.length > 0) {
          genres = genres.filter((g) => g.category?.name === tagCategoryName)
        }
        return genres.length > 0 ? genres.map((g) => g.name).join(', ') : null
      }

      case 'DESCRIPTION': {
        const desc = event?.description ?? guest?.description ?? null
        if (!desc) return null
        const tmp = document.createElement('div')
        tmp.innerHTML = desc
        return tmp.textContent ?? tmp.innerText ?? desc
      }

      case 'DATE_TIME': {
        if (!event) return null
        const dateStr = capitalizeDateString(formatDayLong(event.day, dateFormatConfig))
        const timeStr = showHour(event.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(event, dateFormatConfig, schedule) : ''
        return `${dateStr}${timeStr ? ` ${timeStr}` : ''}`
      }

      case 'SCENE':
        return event?.scene?.name && !event.hideLocation && !config.eventsHideLocation ? event.scene.name : null

      case 'NEXT_EVENT_DATE_TIME': {
        if (!nextEvent) return null
        const dateStr = capitalizeDateString(formatDayLong(nextEvent.day, dateFormatConfig))
        const timeStr = showHour(nextEvent.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(nextEvent, dateFormatConfig, schedule) : ''
        return `${dateStr}${timeStr ? ` ${timeStr}` : ''}`
      }

      case 'NEXT_EVENT_DATE': {
        if (!nextEvent) return null
        return capitalizeDateString(formatDayLong(nextEvent.day, dateFormatConfig))
      }

      case 'NEXT_EVENT_SCENE':
        return nextEvent?.scene?.name && !nextEvent.hideLocation && !config.eventsHideLocation ? nextEvent.scene.name : null

      case 'CONTACT':
        return guest?.contact ?? event?.musicGroups?.[0]?.contact ?? null

      case 'GUEST_TYPE':
        return guest?.guestType?.name ?? event?.musicGroups?.[0]?.guestType?.name ?? null

      case 'NONE':
      default:
        return null
    }
  }

  // Apply layout-level styles
  const cardContainerStyle: React.CSSProperties = {
    ...styles.container,
    ...style,
    // When an info block is rendered, the tile is no longer a fixed-square
    // overlay — it grows in height to fit the block. Keep the aspect ratio
    // only in the legacy overlay-only mode.
    ...(showInfoBlock ? { display: 'flex', flexDirection: 'column', height: '100%' } : { aspectRatio: 1 }),
    transform: isHovered ? 'scale(1.05) rotate(2deg)' : 'scale(1)',
    zIndex: isHovered ? 10 : 0,
    transition: 'all .3s',
  }

  // Group slots by row and resolve values
  type RowName = 'TOP' | 'MIDDLE' | 'BOTTOM'
  type ColName = 'LEFT' | 'CENTER' | 'RIGHT'

  const getRowName = (position: TileSlotPosition): RowName => {
    if (position.startsWith('TOP')) return 'TOP'
    if (position.startsWith('MIDDLE')) return 'MIDDLE'
    return 'BOTTOM'
  }

  const getColName = (position: TileSlotPosition): ColName => {
    if (position.endsWith('LEFT')) return 'LEFT'
    if (position.endsWith('CENTER')) return 'CENTER'
    return 'RIGHT'
  }

  type Cell = { slot: TileSlotConfig; col: ColName; value: React.ReactNode } | null
  const rows = (() => {
    const rowMap: Record<RowName, Cell[]> = {
      TOP: [null, null, null],
      MIDDLE: [null, null, null],
      BOTTOM: [null, null, null],
    }
    const colIndex: Record<ColName, number> = { LEFT: 0, CENTER: 1, RIGHT: 2 }

    for (const slot of layout.slots) {
      if (slot.field === 'NONE') continue
      const value = getFieldValue(slot.field, slot.tagCategoryName ?? undefined)
      if (value === null) continue
      const row = getRowName(slot.position)
      const col = getColName(slot.position)
      rowMap[row][colIndex[col]] = { slot, col, value }
    }

    return rowMap
  })()

  const getTextAlign = (col: ColName): React.CSSProperties['textAlign'] => {
    if (col === 'LEFT') return 'left'
    if (col === 'CENTER') return 'center'
    return 'right'
  }

  const getJustify = (col: ColName): string => {
    if (col === 'LEFT') return 'flex-start'
    if (col === 'CENTER') return 'center'
    return 'flex-end'
  }

  const renderRow = (rowName: RowName): React.ReactNode => {
    const items = rows[rowName]
    const filledCount = items.reduce((acc, c) => acc + (c ? 1 : 0), 0)
    if (filledCount === 0) return null

    const rowStyle: React.CSSProperties = {
      position: 'absolute',
      left: 0,
      right: 0,
      display: 'flex',
      flexWrap: 'nowrap',
      ...(rowName === 'TOP' ? { top: 0 } : rowName === 'BOTTOM' ? { bottom: 0 } : { top: '50%', transform: 'translateY(-50%)' }),
    }

    const singleCell = filledCount === 1

    const rowAlignItems: React.CSSProperties['alignItems'] = rowName === 'TOP' ? 'flex-start' : rowName === 'BOTTOM' ? 'flex-end' : 'center'

    return (
      <div style={rowStyle}>
        {items.map((cell, index) => {
          if (singleCell && !cell) return null

          const cellStyle: React.CSSProperties = {
            flex: singleCell ? '1 1 100%' : '1 1 33.3333%',
            maxWidth: singleCell ? '100%' : '33.3333%',
            display: 'flex',
            alignItems: rowAlignItems,
            justifyContent: cell ? getJustify(cell.col) : 'center',
            padding: '8px',
            minWidth: 0,
            overflow: 'hidden',
          }

          if (!cell) {
            return <div key={index} style={cellStyle} />
          }

          const { slot, col, value } = cell
          const hasBackground = !!slot.backgroundColor && !isFullyTransparent(slot.backgroundColor)

          const textStyle: React.CSSProperties = {
            color: slot.font?.color ?? 'white',
            fontSize: slot.font?.size ? `${slot.font.size}px` : '1em',
            fontFamily: slot.font?.family,
            fontWeight: slot.font?.weight,
            textAlign: getTextAlign(col),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...(hasBackground
              ? {
                  backgroundColor: slot.backgroundColor,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  borderRadius: '6px',
                  padding: '4px 10px',
                }
              : {}),
          }

          return (
            <div key={index} style={cellStyle}>
              <span style={textStyle}>{value}</span>
            </div>
          )
        })}
      </div>
    )
  }

  const noImageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: layout.fallbackColor ?? '#667eea',
  }

  // Resolve a single info block element to a React node (or null when empty,
  // so the row is skipped entirely).
  const renderInfoBlockElement = (element: InfoBlockElementType): React.ReactNode => {
    switch (element) {
      case 'TITLE': {
        const title = event?.title ?? guest?.name
        if (!title) return null
        return <div style={styles.infoBlockTitle}>{title}</div>
      }
      case 'DATE_TIME': {
        if (!event) return null
        const dateStr = capitalizeDateString(formatDayLong(event.day, dateFormatConfig))
        const timeStr = showHour(event.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(event, dateFormatConfig, schedule) : ''
        return <div style={styles.infoBlockLine}>{`${dateStr}${timeStr ? ` ${timeStr}` : ''}`}</div>
      }
      case 'DATE': {
        if (!event) return null
        return <div style={styles.infoBlockLine}>{capitalizeDateString(formatDayLong(event.day, dateFormatConfig))}</div>
      }
      case 'TIME': {
        if (!event) return null
        if (!showHour(event.showStartDate, schedule, nowMs())) return null
        return <div style={styles.infoBlockLine}>{formatHour(event.showStartDate, dateFormatConfig)}</div>
      }
      case 'SCENE': {
        if (!event?.scene?.name || event.hideLocation || config.eventsHideLocation) return null
        return <div style={styles.infoBlockLine}>{event.scene.name}</div>
      }
      case 'PRICE': {
        if (!event?.price) return null
        return <div style={styles.infoBlockLine}>{event.price}</div>
      }
      case 'GUESTS': {
        // GUESTS as a text line (comma-separated names). Pastilles are a
        // separate feature on the tile body — toggled via showEventGuests.
        if (!event?.musicGroups?.length) return null
        return <div style={styles.infoBlockLine}>{event.musicGroups.map((g) => g.name).join(', ')}</div>
      }
      default:
        return null
    }
  }

  const renderInfoBlock = (): React.ReactNode => {
    if (!showInfoBlock) return null
    const defaults = event ? DEFAULT_INFO_BLOCK_ELEMENTS_EVENT : DEFAULT_INFO_BLOCK_ELEMENTS_GUEST
    const elements = layout.infoBlockElements ?? defaults
    const lines = elements
      .filter((e) => e.enabled)
      .map((e, idx) => {
        const node = renderInfoBlockElement(e.type)
        return node ? <React.Fragment key={`${e.type}-${idx}`}>{node}</React.Fragment> : null
      })
      .filter(Boolean)

    const blockStyle: React.CSSProperties = {
      ...styles.infoBlock,
      ...(layout.infoBlockBackgroundColor ? { backgroundColor: layout.infoBlockBackgroundColor } : {}),
    }
    const pastilles = renderGuestPastilleBubbles()
    return (
      <div style={blockStyle}>
        {lines}
        {pastilles ? <div style={styles.infoBlockPastilles}>{pastilles}</div> : null}
      </div>
    )
  }

  // The overlapping guest bubbles themselves (without their positioning
  // wrapper), so they can be placed either as an overlay on the image
  // (legacy mode) or inside the info block (preferred — never covers the logo).
  const renderGuestPastilleBubbles = (): React.ReactNode => {
    if (!showEventGuests || !event?.musicGroups?.length) return null
    const visible = event.musicGroups.slice(0, 3)
    const extra = event.musicGroups.length - visible.length
    return (
      <>
        {visible.map((g, idx) => (
          <div key={g._id} style={{ ...styles.pastille, marginLeft: idx === 0 ? 0 : -8, zIndex: visible.length - idx }} title={g.name}>
            {g.image ? (
              <ResourceImage imageId={g.image} style={styles.pastilleImage} alt={g.name} />
            ) : (
              <div style={styles.pastilleFallback}>{initialsOf(g.name)}</div>
            )}
          </div>
        ))}
        {extra > 0 && <div style={{ ...styles.pastille, ...styles.pastilleMore, marginLeft: -8 }}>+{extra}</div>}
      </>
    )
  }

  // Legacy overlay placement, used only when there is no info block to host
  // the bubbles. Sits over the bottom-left of the image.
  const renderGuestPastillesOverlay = (): React.ReactNode => {
    const bubbles = renderGuestPastilleBubbles()
    if (!bubbles) return null
    return <div style={styles.pastillesContainer}>{bubbles}</div>
  }

  // Renders the image / overlay portion of the tile. In info-block mode it
  // keeps a square aspect ratio so the block below it lines up nicely; in
  // legacy mode it fills the whole card (the card itself owns the ratio).
  const renderImageArea = (): React.ReactNode => (
    <div style={showInfoBlock ? styles.imageAreaWithBlock : styles.fullImageContainer}>
      {image ? <ResourceImage imageId={image} style={styles.image} alt="" /> : <div style={noImageStyle} />}
      {renderRow('TOP')}
      {renderRow('MIDDLE')}
      {renderRow('BOTTOM')}
      {/* In info-block mode the bubbles live inside the block (see renderInfoBlock)
          so they never overlap the logo; otherwise fall back to the image overlay. */}
      {!showInfoBlock && renderGuestPastillesOverlay()}
    </div>
  )

  const cardContent = (
    <Card style={cardContainerStyle}>
      {renderImageArea()}
      {renderInfoBlock()}
    </Card>
  )

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {navLink ? <NavigationLink path={navLink}>{cardContent}</NavigationLink> : cardContent}
    </div>
  )
})

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

// Returns true for #rrggbb00 — the only hex form that means "fully see-through"
// in the BO ColorPicker (hexa mode). Other lengths/formats are treated as
// non-transparent.
function isFullyTransparent(color: string): boolean {
  return /^#[0-9a-f]{6}00$/i.test(color)
}

function capitalizeDateString(text: string): string {
  return text
    .split(' ')
    .map((part) => (part.length > 0 ? part[0].toUpperCase() + part.substring(1) : ''))
    .join(' ')
}

const styles = StyleSheet.create({
  container: {
    padding: 0,
    overflow: 'hidden',
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  fullImageContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  imageAreaWithBlock: {
    position: 'relative',
    width: '100%',
    aspectRatio: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },

  infoBlock: {
    padding: '.8em',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flexGrow: 1,
  },

  infoBlockTitle: {
    fontWeight: 'bold',
    fontSize: '1em',
    marginBottom: 4,
  },

  infoBlockLine: {
    fontSize: '.85em',
  },

  infoBlockPastilles: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },

  pastillesContainer: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },

  pastille: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    overflow: 'hidden',
    border: '2px solid white',
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
    backgroundColor: '#ddd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  pastilleImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  pastilleFallback: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#666',
  },

  pastilleMore: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333',
    backgroundColor: '#f0f0f0',
  },
})
